import React, { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams, useLocation } from 'react-router-dom';
import api from '../lib/api';
import { fmtMoney, fmtPrice } from '../utils/currency';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Checkbox } from '../components/ui/checkbox';
import { groupCheckedValue, toggleGroup } from '../utils/invoiceSelection';

// Soft-cyan selection styling for checkboxes (checked + mixed), AA-readable in
// both themes, with a clearly visible keyboard focus ring.
// Dark (#041019) check/indeterminate mark on #37c9ee ≈ 9.8:1 contrast (AA).
const CYAN_CHECK = 'data-[state=checked]:bg-[#37c9ee] data-[state=checked]:border-[#37c9ee] data-[state=checked]:text-[#041019] data-[state=indeterminate]:bg-[#37c9ee] data-[state=indeterminate]:border-[#37c9ee] data-[state=indeterminate]:text-[#041019] focus-visible:ring-2 focus-visible:ring-[#37c9ee] focus-visible:ring-offset-1';
// Selected row/card tint handled via scoped CSS (index.css) so it survives
// shadcn's data-[state=selected]:bg-muted + hover in both the light fixture
// and the live .co-scope dark portal (which has no .dark ancestor).
const CYAN_ROW_SELECTED = 'inv-selected-row';
import { Separator } from '../components/ui/separator';
import {
  Plus, Search, FileText, Trash2, Edit, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, ExternalLink, Filter, X,
  RefreshCw, Repeat, User, Wrench, DollarSign, Loader2,
  CheckSquare, Square, Send, Ban, Download, CreditCard, Mail, Eye,
  Users, ChevronRight as ChevronRightIcon
} from 'lucide-react';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { formatDateOnly } from '../utils/timezone';
import { compareInvoices } from '../utils/sortHelpers';
import { buildReturnParam } from '../utils/returnNav';
import InvoiceSendButton from '../components/InvoiceSendButton';

export default function InvoicesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [invoices, setInvoices] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [modalPreviewUrl, setModalPreviewUrl] = useState(null);
  const [modalPreviewOpen, setModalPreviewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [editingInvoice, setEditingInvoice] = useState(false);
  const [recurringDialogOpen, setRecurringDialogOpen] = useState(false);

  // Filters
  const [showFilters, setShowFilters] = useState(true);
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || 'unpaid',
    customer_id: searchParams.get('customer_id') || '',
    is_recurring: searchParams.get('is_recurring') || '',
  });

  // Pagination
  const [currentPage, setCurrentPage] = useState(0);
  const [pageSize, setPageSize] = useState(25);
  const [totalInvoices, setTotalInvoices] = useState(0);

  // Sorting
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'created_at');
  const [sortOrder, setSortOrder] = useState(searchParams.get('dir') || 'desc');
  const [expandedGroups, setExpandedGroups] = useState({});
  const [groupSort, setGroupSort] = useState({ field: 'due_date', order: 'asc' });

  // Recurring form
  const [recurringData, setRecurringData] = useState({
    recurring_frequency: 'monthly',
    recurring_start_date: new Date().toISOString().split('T')[0],
    recurring_end_date: ''
  });

  const [repairing, setRepairing] = useState(false);
  const [bulkSyncingWhmcs, setBulkSyncingWhmcs] = useState(false);
  const [whmcsSyncProgress, setWhmcsSyncProgress] = useState(null);
  const [applyingCredit, setApplyingCredit] = useState(false);
  const [creditBalance, setCreditBalance] = useState(null);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkAction, setBulkAction] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkPaymentMethod, setBulkPaymentMethod] = useState('');
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [reminderConfig, setReminderConfig] = useState(null);
  const [reminderSettingsOpen, setReminderSettingsOpen] = useState(false);

  const handleApplyCreditToInvoice = async (invoice) => {
    if (!invoice?.customer_id) { toast.error('Invoice has no customer'); return; }
    setApplyingCredit(true);
    try {
      const balRes = await api.get(`/customers/${invoice.customer_id}/credit-balance`);
      const bal = balRes.data.available_credit || 0;
      if (bal <= 0) { toast.error('No available credit for this customer'); setApplyingCredit(false); return; }
      const invoiceRemaining = (invoice.total || 0) - (invoice.credits_applied || 0);
      const maxApply = Math.min(bal, invoiceRemaining);
      const amountStr = window.prompt(`Apply credit to Invoice #${invoice.invoice_number}\n\nAvailable credit: $${bal.toFixed(2)}\nInvoice remaining: $${invoiceRemaining.toFixed(2)}\n\nEnter amount to apply:`, maxApply.toFixed(2));
      if (!amountStr) { setApplyingCredit(false); return; }
      const amount = parseFloat(amountStr);
      if (isNaN(amount) || amount <= 0) { toast.error('Invalid amount'); setApplyingCredit(false); return; }
      const res = await api.post(`/customers/${invoice.customer_id}/credits/apply`, { invoice_id: invoice.id, amount });
      toast.success(res.data.message);
      loadInvoices();
      if (selectedInvoice?.id === invoice.id) {
        setSelectedInvoice({ ...selectedInvoice, credits_applied: (selectedInvoice.credits_applied || 0) + res.data.applied, status: res.data.invoice_status });
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply credit');
    }
    setApplyingCredit(false);
  };

  const loadCreditBalance = async (customerId) => {
    if (!customerId) return;
    try {
      const res = await api.get(`/customers/${customerId}/credit-balance`);
      setCreditBalance(res.data.available_credit || 0);
    } catch { setCreditBalance(null); }
  };

  const handleRepairCustomerLinks = async () => {
    setRepairing(true);
    try {
      const response = await api.post('/invoices/repair-customer-links');
      const data = response.data;
      if (data.repaired > 0) {
        toast.success(`Repaired ${data.repaired} invoice(s)`);
        loadInvoices();
      } else {
        toast.info('All invoices already have customer links');
      }
      if (data.unresolvable > 0) {
        toast(`${data.unresolvable} invoice(s) could not be resolved — missing customer ID. Try syncing customers from WHMCS first.`, { duration: 6000 });
      }
    } catch (error) {
      toast.error('Failed to repair customer links');
    } finally {
      setRepairing(false);
    }
  };

  const handleBulkSyncWhmcsTransactions = async () => {
    setBulkSyncingWhmcs(true);
    try {
      const response = await api.post('/invoices/bulk-sync-whmcs-transactions');
      const data = response.data;
      if (data.already_running) {
        toast.info('Sync already in progress');
      } else if (data.total === 0) {
        toast.info(data.message);
        setBulkSyncingWhmcs(false);
        return;
      } else {
        setWhmcsSyncProgress({ status: 'running', total: data.total, synced: 0, failed: 0 });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Bulk WHMCS sync failed');
      setBulkSyncingWhmcs(false);
    }
  };

  // Poll for sync progress
  useEffect(() => {
    if (!bulkSyncingWhmcs) return;
    const interval = setInterval(async () => {
      try {
        const res = await api.get('/invoices/whmcs-sync-status');
        const p = res.data;
        setWhmcsSyncProgress(p);
        if (p.status === 'completed' || p.status === 'idle') {
          setBulkSyncingWhmcs(false);
          if (p.status === 'completed') {
            toast.success(`Sync complete: ${p.synced} synced${p.failed > 0 ? `, ${p.failed} failed` : ''}`);
            loadInvoices();
          }
        }
      } catch {
        // ignore polling errors
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [bulkSyncingWhmcs]);

  // Check on mount if a sync is already running
  useEffect(() => {
    const checkRunningSync = async () => {
      try {
        const res = await api.get('/invoices/whmcs-sync-status');
        if (res.data.status === 'running') {
          setBulkSyncingWhmcs(true);
          setWhmcsSyncProgress(res.data);
        }
      } catch {}
    };
    checkRunningSync();
  }, []);

  const toggleSelect = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === invoices.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(invoices.map(i => i.id)));
    }
  };

  const handleBulkAction = async () => {
    if (!bulkAction || selectedIds.size === 0) return;
    setBulkLoading(true);
    try {
      const payload = { action: bulkAction, invoice_ids: [...selectedIds] };
      if (bulkAction === 'mark_paid') payload.payment_method = bulkPaymentMethod;

      if (bulkAction === 'export_csv') {
        const res = await api.post('/invoices/bulk-action', payload);
        const csvData = res.data.csv_data || [];
        if (csvData.length === 0) { toast.error('No data to export'); setBulkLoading(false); return; }
        const headers = Object.keys(csvData[0]);
        const csv = [headers.join(','), ...csvData.map(r => headers.map(h => `"${(r[h] ?? '')}"`).join(','))].join('\n');
        const blob = new Blob([csv], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = `invoices_export_${new Date().toISOString().slice(0,10)}.csv`; a.click();
        URL.revokeObjectURL(url);
        toast.success(`Exported ${csvData.length} invoices`);
      } else {
        const res = await api.post('/invoices/bulk-action', payload);
        const d = res.data;
        if (d.success > 0) toast.success(`${d.success} invoice(s) updated`);
        if (d.failed > 0) toast.error(`${d.failed} invoice(s) failed`);
        loadInvoices();
      }
      setSelectedIds(new Set());
      setBulkAction('');
      setBulkConfirmOpen(false);
      setBulkPaymentMethod('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk action failed');
    }
    setBulkLoading(false);
  };

  const confirmBulkAction = () => {
    if (bulkAction === 'delete' || bulkAction === 'void' || bulkAction === 'send_reminder') {
      setBulkConfirmOpen(true);
    } else {
      handleBulkAction();
    }
  };

  const bulkActionLabels = {
    mark_paid: 'Mark as Paid',
    mark_sent: 'Mark as Sent',
    apply_credit: 'Apply Credit',
    void: 'Void / Cancel',
    delete: 'Delete',
    send_reminder: 'Send Payment Reminder',
    export_csv: 'Export to CSV',
  };

  const [formData, setFormData] = useState({
    customer_id: '',
    issue_date: new Date().toISOString().split('T')[0],
    due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    status: 'draft',
    line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0, tax_exempt: false }],
    notes: '',
    is_recurring: false,
    recurring_frequency: 'monthly',
    recurring_start_date: new Date().toISOString().split('T')[0],
    recurring_end_date: ''
  });

  useEffect(() => { loadCustomers(); loadReminderConfig(); }, []);

  const loadReminderConfig = async () => {
    try {
      const res = await api.get('/settings/payment-reminders');
      setReminderConfig(res.data);
    } catch {}
  };

  const saveReminderConfig = async (updates) => {
    try {
      await api.put('/settings/payment-reminders', updates);
      setReminderConfig(prev => ({ ...prev, ...updates }));
      toast.success('Reminder settings saved');
    } catch { toast.error('Failed to save'); }
  };

  useEffect(() => {
    loadInvoices();
  }, [filters, searchTerm, currentPage, pageSize, sortBy, sortOrder]);

  useEffect(() => {
    setCurrentPage(0);
  }, [filters, searchTerm]);

  // Keep filters/sort in the URL so refresh and origin-aware back links restore them
  useEffect(() => {
    const next = {};
    if (filters.status) next.status = filters.status;
    if (filters.customer_id) next.customer_id = filters.customer_id;
    if (filters.is_recurring) next.is_recurring = filters.is_recurring;
    if (sortBy && sortBy !== 'created_at') next.sort = sortBy;
    if (sortOrder && sortOrder !== 'desc') next.dir = sortOrder;
    setSearchParams(next, { replace: true });
  }, [filters, sortBy, sortOrder, setSearchParams]);

  const loadInvoices = async () => {
    try {
      const params = {
        skip: currentPage * pageSize,
        limit: pageSize,
        sort_by: sortBy,
        sort_order: sortOrder,
      };
      if (filters.status) params.status = filters.status;
      if (filters.customer_id) params.customer_id = filters.customer_id;
      if (searchTerm) params.search = searchTerm;

      const response = await api.get('/invoices', { params });
      const data = response.data;
      if (data && data.invoices) {
        let invList = data.invoices;
        // Client-side filter for recurring if set
        if (filters.is_recurring === 'true') {
          invList = invList.filter(i => i.is_recurring);
        } else if (filters.is_recurring === 'false') {
          invList = invList.filter(i => !i.is_recurring);
        }
        setInvoices(invList);
        setTotalInvoices(data.total || 0);
      } else if (Array.isArray(data)) {
        setInvoices(data);
        setTotalInvoices(data.length);
      }
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadCustomers = async () => {
    try {
      const response = await api.get('/customers', { params: { limit: 10000, status: 'active' } });
      const custData = response.data;
      setCustomers(Array.isArray(custData) ? custData : custData.customers || []);
    } catch (error) {
      console.error('Failed to load customers');
    }
  };

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  const SortIcon = ({ field }) => {
    if (sortBy !== field) return <ArrowUpDown className="h-3.5 w-3.5 text-neutral-400" />;
    return sortOrder === 'asc'
      ? <ArrowUp className="h-3.5 w-3.5 text-blue-600" />
      : <ArrowDown className="h-3.5 w-3.5 text-blue-600" />;
  };

  const hasActiveFilters = filters.status || filters.customer_id || filters.is_recurring;

  const clearFilters = () => {
    setFilters({ status: '', customer_id: '', is_recurring: '' });
  };

  const updateLineItem = (index, field, value) => {
    const newItems = [...formData.line_items];
    newItems[index][field] = value;
    if (field === 'quantity' || field === 'unit_price') {
      const raw = (parseFloat(newItems[index].quantity) || 0) * (parseFloat(newItems[index].unit_price) || 0);
      // Truncate to 2 decimal places (floor), do NOT round up
      newItems[index].amount = Math.floor(raw * 100) / 100;
    }
    setFormData({ ...formData, line_items: newItems });
  };

  const addLineItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: '', quantity: 1, unit_price: 0, amount: 0, tax_exempt: false, is_reimbursement: false }]
    });
  };

  const addReimbursementItem = () => {
    setFormData({
      ...formData,
      line_items: [...formData.line_items, { description: 'Reimbursement: ', quantity: 1, unit_price: 0, amount: 0, tax_exempt: true, is_reimbursement: true, receipt_file: null, receipt_name: '' }]
    });
  };

  const [receiptFiles, setReceiptFiles] = useState({});  // index -> File object

  const handleReceiptUpload = (index, file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) { toast.error('Receipt must be under 10MB'); return; }
    setReceiptFiles(prev => ({ ...prev, [index]: file }));
    updateLineItem(index, 'receipt_name', file.name);
  };

  const removeLineItem = (index) => {
    if (formData.line_items.length > 1) {
      setFormData({ ...formData, line_items: formData.line_items.filter((_, i) => i !== index) });
    }
  };

  const calculateTotals = () => {
    const subtotal = formData.line_items.reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxRate = 0.0825;
    const taxableAmount = formData.line_items
      .filter(item => !item.tax_exempt)
      .reduce((sum, item) => sum + (item.amount || 0), 0);
    const taxAmount = taxableAmount * taxRate;
    const total = subtotal + taxAmount;
    return { subtotal, taxRate, taxAmount, total, taxableAmount };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (editingInvoice && selectedInvoice) return handleUpdateInvoice(e);
    try {
      const customer = customers.find(c => c.id === formData.customer_id);
      const { subtotal, taxRate, taxAmount, total } = calculateTotals();
      const dataToSubmit = {
        customer_id: formData.customer_id,
        customer_name: customer?.company_name || '',
        issue_date: formData.issue_date,
        due_date: formData.due_date || null,
        status: formData.status,
        line_items: formData.line_items,
        subtotal, tax_rate: taxRate, tax_amount: taxAmount, total,
        notes: formData.notes || null,
        is_recurring: formData.is_recurring,
        recurring_frequency: formData.is_recurring ? formData.recurring_frequency : null,
        recurring_start_date: formData.is_recurring ? formData.recurring_start_date : null,
        recurring_end_date: formData.is_recurring && formData.recurring_end_date ? formData.recurring_end_date : null,
      };
      const res = await api.post('/invoices', dataToSubmit);
      const newInvoiceId = res.data?.id;

      // Upload receipts for reimbursement items
      if (newInvoiceId && Object.keys(receiptFiles).length > 0) {
        for (const [idx, file] of Object.entries(receiptFiles)) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('line_item_index', idx);
            await api.post(`/invoices/${newInvoiceId}/receipts`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          } catch (err) { console.error('Receipt upload failed:', err); }
        }
      }

      toast.success('Invoice created successfully');
      setDialogOpen(false);
      resetForm();
      loadInvoices();
    } catch (error) {
      toast.error('Failed to create invoice');
    }
  };

  const handleDelete = async (invoiceId) => {
    if (!window.confirm('Are you sure you want to delete this invoice?')) return;
    try {
      await api.delete(`/invoices/${invoiceId}`);
      toast.success('Invoice deleted successfully');
      loadInvoices();
    } catch (error) {
      toast.error('Failed to delete invoice');
    }
  };

  const resetForm = () => {
    setFormData({
      customer_id: '',
      issue_date: new Date().toISOString().split('T')[0],
      due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      status: 'draft',
      line_items: [{ description: '', quantity: 1, unit_price: 0, amount: 0, tax_exempt: false }],
      notes: '',
      is_recurring: false,
      recurring_frequency: 'monthly',
      recurring_start_date: new Date().toISOString().split('T')[0],
      recurring_end_date: ''
    });
  };

  const handleViewInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setDetailDialogOpen(true);
    setEditingInvoice(false);
  };

  const handleEditInvoice = () => {
    if (selectedInvoice) {
      setFormData({
        customer_id: selectedInvoice.customer_id,
        issue_date: selectedInvoice.issue_date?.split('T')[0] || '',
        due_date: selectedInvoice.due_date?.split('T')[0] || '',
        status: selectedInvoice.status,
        line_items: selectedInvoice.line_items?.length > 0 ? selectedInvoice.line_items : [{ description: '', quantity: 1, unit_price: 0, amount: 0, tax_exempt: false }],
        notes: selectedInvoice.notes || '',
        is_recurring: selectedInvoice.is_recurring || false,
        recurring_frequency: selectedInvoice.recurring_frequency || 'monthly',
        recurring_start_date: selectedInvoice.recurring_start_date?.split('T')[0] || new Date().toISOString().split('T')[0],
        recurring_end_date: selectedInvoice.recurring_end_date?.split('T')[0] || '',
      });
      setEditingInvoice(true);
      setDetailDialogOpen(false);
      setDialogOpen(true);
    }
  };

  const handleUpdateInvoice = async (e) => {
    e.preventDefault();
    try {
      const customer = customers.find(c => c.id === formData.customer_id);
      const { subtotal, taxRate, taxAmount, total } = calculateTotals();
      const dataToSubmit = {
        ...formData,
        customer_name: customer?.company_name,
        subtotal, tax_rate: taxRate, tax_amount: taxAmount, total
      };
      await api.put(`/invoices/${selectedInvoice.id}`, dataToSubmit);
      toast.success('Invoice updated successfully');
      setDialogOpen(false);
      setEditingInvoice(false);
      resetForm();
      loadInvoices();
    } catch (error) {
      toast.error('Failed to update invoice');
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      await api.put(`/invoices/${invoiceId}`, { status: newStatus });
      toast.success(`Invoice status updated to ${newStatus}`);
      loadInvoices();
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice({ ...selectedInvoice, status: newStatus });
      }
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleMakeRecurring = async () => {
    if (!selectedInvoice) return;
    try {
      await api.put(`/invoices/${selectedInvoice.id}`, {
        is_recurring: true,
        recurring_frequency: recurringData.recurring_frequency,
        recurring_start_date: recurringData.recurring_start_date,
        recurring_end_date: recurringData.recurring_end_date || null,
      });
      toast.success('Invoice set as recurring');
      setRecurringDialogOpen(false);
      setSelectedInvoice({ ...selectedInvoice, is_recurring: true, recurring_frequency: recurringData.recurring_frequency });
      loadInvoices();
    } catch (error) {
      toast.error('Failed to update recurring settings');
    }
  };

  const handleRemoveRecurring = async (invoiceId) => {
    try {
      await api.put(`/invoices/${invoiceId}`, {
        is_recurring: false,
        recurring_frequency: null,
        recurring_start_date: null,
        recurring_end_date: null,
      });
      toast.success('Recurring removed');
      if (selectedInvoice?.id === invoiceId) {
        setSelectedInvoice({ ...selectedInvoice, is_recurring: false });
      }
      loadInvoices();
    } catch (error) {
      toast.error('Failed to remove recurring');
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      draft: 'bg-gray-100 text-gray-800',
      sent: 'bg-blue-100 text-blue-800',
      paid: 'bg-green-100 text-green-800',
      overdue: 'bg-red-100 text-red-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || colors.draft;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return formatDateOnly(dateStr, 'MMM d, yyyy'); } catch { return dateStr; }
  };

  const { subtotal: calcSubtotal, taxAmount: calcTax, total: calcTotal, taxableAmount } = calculateTotals();
  const totalPages = Math.max(1, Math.ceil(totalInvoices / pageSize));

  if (loading) {
    return (
      <div className="co-page co-scope co-settings flex flex-col items-center justify-center min-h-[60vh]">
        <img src="/cipherone-logo.png" alt="Loading" className="h-16 w-16 logo-spin mb-4" />
        <div className="text-lg text-gray-600">Loading invoices...</div>
      </div>
    );
  }

  return (
    <div className="co-page co-scope co-settings space-y-4 sm:space-y-6" data-testid="invoices-page">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold">Invoices</h1>
          <p className="text-gray-600 text-sm sm:text-base mt-1">Create and manage customer invoices</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Button
              variant="outline"
              onClick={handleBulkSyncWhmcsTransactions}
              disabled={bulkSyncingWhmcs}
              data-testid="bulk-sync-whmcs-btn"
            >
              {bulkSyncingWhmcs ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
              {bulkSyncingWhmcs && whmcsSyncProgress?.total
                ? `${whmcsSyncProgress.synced + whmcsSyncProgress.failed} / ${whmcsSyncProgress.total}`
                : bulkSyncingWhmcs ? 'Syncing...' : 'Sync All WHMCS'}
            </Button>
            {bulkSyncingWhmcs && whmcsSyncProgress?.total > 0 && (
              <div className="absolute bottom-0 left-0 right-0 h-[3px] bg-neutral-200 rounded-b overflow-hidden" data-testid="whmcs-sync-progress-bar">
                <div
                  className="h-full bg-[#1a5490] transition-all duration-500 ease-out"
                  style={{ width: `${Math.round(((whmcsSyncProgress.synced + whmcsSyncProgress.failed) / whmcsSyncProgress.total) * 100)}%` }}
                />
              </div>
            )}
          </div>
          <Button
            variant="outline"
            onClick={handleRepairCustomerLinks}
            disabled={repairing}
            data-testid="repair-customer-links-btn"
          >
            <Wrench className={`h-4 w-4 mr-2 ${repairing ? 'animate-spin' : ''}`} />
            {repairing ? 'Repairing...' : 'Repair Customer Links'}
          </Button>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-invoice-button">
              <Plus className="h-4 w-4 mr-2" />
              New Invoice
            </Button>
          </DialogTrigger>
          <DialogContent className="co-scope co-settings w-[95vw] max-w-4xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg">{editingInvoice ? 'Edit Invoice' : 'Create New Invoice'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3 sm:gap-4">
                <div>
                  <Label className="text-xs sm:text-sm">Customer *</Label>
                  <Select value={formData.customer_id} onValueChange={(value) => setFormData({...formData, customer_id: value})} required>
                    <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm"><SelectValue placeholder="Select customer" /></SelectTrigger>
                    <SelectContent>
                      {customers.map((customer) => (
                        <SelectItem key={customer.id} value={customer.id}>{customer.company_name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm">Issue Date *</Label>
                  <Input type="date" className="h-8 sm:h-9 text-xs sm:text-sm" value={formData.issue_date} onChange={(e) => setFormData({...formData, issue_date: e.target.value})} required />
                </div>
                <div>
                  <Label className="text-xs sm:text-sm">Due Date</Label>
                  <Input type="date" className="h-8 sm:h-9 text-xs sm:text-sm" value={formData.due_date} onChange={(e) => setFormData({...formData, due_date: e.target.value})} />
                </div>
              </div>

              <div>
                <div className="flex justify-between items-center mb-2">
                  <Label className="text-xs sm:text-sm">Line Items</Label>
                  <div className="flex gap-1">
                    <Button type="button" variant="outline" size="sm" className="h-7 sm:h-8 text-xs" onClick={addReimbursementItem} data-testid="add-reimbursement-btn">
                      <CreditCard className="h-3 w-3 mr-1" /> Reimbursement
                    </Button>
                    <Button type="button" variant="outline" size="sm" className="h-7 sm:h-8 text-xs" onClick={addLineItem}>
                      <Plus className="h-3 w-3 mr-1" /> Add
                    </Button>
                  </div>
                </div>
                {/* Mobile: Stacked layout */}
                <div className="sm:hidden space-y-3">
                  {formData.line_items.map((item, index) => (
                    <div key={index} className="p-3 bg-gray-50 rounded-lg space-y-2">
                      <div className="flex justify-between items-center">
                        <span className="text-xs font-semibold text-gray-600">Item {index + 1}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => removeLineItem(index)} className="h-6 w-6 p-0">
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                      <Input className="h-8 text-xs" placeholder="Description" value={item.description} onChange={(e) => updateLineItem(index, 'description', e.target.value)} />
                      <div className="grid grid-cols-3 gap-2">
                        <div>
                          <Label className="text-[10px] text-gray-500">Qty</Label>
                          <Input className="h-7 text-xs" type="number" value={item.quantity} onChange={(e) => updateLineItem(index, 'quantity', e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-gray-500">Price</Label>
                          <Input className="h-7 text-xs" type="number" step="0.001" value={item.unit_price} onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)} />
                        </div>
                        <div>
                          <Label className="text-[10px] text-gray-500">Total</Label>
                          <Input className="h-7 text-xs bg-gray-100" type="number" value={item.amount.toFixed(2)} readOnly />
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Checkbox checked={item.tax_exempt} onCheckedChange={(checked) => updateLineItem(index, 'tax_exempt', checked)} />
                        <span className="text-xs text-gray-600">Non-taxable</span>
                      </div>
                      {item.is_reimbursement && (
                        <div className="border border-dashed border-amber-300 rounded p-2 bg-amber-50">
                          <Label className="text-[10px] text-amber-700 font-semibold">Receipt Required</Label>
                          <input type="file" accept="image/*,.pdf" className="block w-full text-xs mt-1" onChange={(e) => handleReceiptUpload(index, e.target.files?.[0])} />
                          {(receiptFiles[index] || item.receipt_name) && <p className="text-[10px] text-green-700 mt-1">Attached: {receiptFiles[index]?.name || item.receipt_name}</p>}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                {/* Desktop: Grid layout */}
                <div className="hidden sm:block">
                  <div className="grid grid-cols-12 gap-2 items-center mb-2 px-1">
                    <div className="col-span-5 text-xs font-semibold text-gray-600">Description</div>
                    <div className="col-span-1 text-xs font-semibold text-gray-600">Qty</div>
                    <div className="col-span-2 text-xs font-semibold text-gray-600">Price per Item</div>
                    <div className="col-span-2 text-xs font-semibold text-gray-600">Total</div>
                    <div className="col-span-1 text-xs font-semibold text-gray-600 text-center">NT</div>
                    <div className="col-span-1"></div>
                  </div>
                  <div className="space-y-2">
                    {formData.line_items.map((item, index) => (
                      <React.Fragment key={index}>
                      <div className="grid grid-cols-12 gap-2 items-center">
                        <Input className="col-span-5" placeholder="Description" value={item.description} onChange={(e) => updateLineItem(index, 'description', e.target.value)} />
                        <Input className="col-span-1" type="number" placeholder="Qty" value={item.quantity} onChange={(e) => updateLineItem(index, 'quantity', e.target.value)} />
                        <Input className="col-span-2" type="number" step="0.001" placeholder="Price" value={item.unit_price} onChange={(e) => updateLineItem(index, 'unit_price', e.target.value)} />
                        <Input className="col-span-2" type="number" value={item.amount.toFixed(2)} readOnly />
                        <div className="col-span-1 flex items-center justify-center">
                          <Checkbox checked={item.tax_exempt} onCheckedChange={(checked) => updateLineItem(index, 'tax_exempt', checked)} />
                        </div>
                        <Button type="button" variant="ghost" size="sm" onClick={() => { removeLineItem(index); setReceiptFiles(p => { const n = {...p}; delete n[index]; return n; }); }} className="col-span-1">
                          <Trash2 className="h-3 w-3 text-red-500" />
                        </Button>
                      </div>
                      {item.is_reimbursement && (
                        <div className="ml-2 mb-1 flex items-center gap-2 border border-dashed border-amber-300 rounded px-3 py-1.5 bg-amber-50">
                          <CreditCard className="h-3.5 w-3.5 text-amber-600 shrink-0" />
                          <span className="text-[11px] text-amber-700 font-medium shrink-0">Receipt:</span>
                          <input type="file" accept="image/*,.pdf" className="text-xs flex-1" onChange={(e) => handleReceiptUpload(index, e.target.files?.[0])} data-testid={`receipt-upload-${index}`} />
                          {(receiptFiles[index] || item.receipt_name) && <span className="text-[11px] text-green-700 shrink-0">{receiptFiles[index]?.name || item.receipt_name}</span>}
                        </div>
                      )}
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 p-2 sm:p-3 rounded-lg space-y-1 text-xs sm:text-sm">
                <div className="flex justify-between"><span>Subtotal:</span><span className="font-semibold">${fmtMoney(calcSubtotal)}</span></div>
                <div className="flex justify-between"><span>Tax (8.25% on ${fmtMoney(taxableAmount)}):</span><span>${fmtMoney(calcTax)}</span></div>
                <div className="flex justify-between font-bold border-t pt-1"><span>Total:</span><span>${fmtMoney(calcTotal)}</span></div>
              </div>

              {/* Recurring Option in Create/Edit Form */}
              <div className="flex items-center gap-2 sm:gap-3 p-2 sm:p-3 bg-neutral-50 rounded-lg border">
                <Checkbox
                  id="form-recurring"
                  checked={formData.is_recurring}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_recurring: checked })}
                />
                <Label htmlFor="form-recurring" className="cursor-pointer flex items-center gap-2 text-xs sm:text-sm">
                  <Repeat className="h-3 w-3 sm:h-4 sm:w-4 text-neutral-500" />
                  Make recurring
                </Label>
              </div>
              {formData.is_recurring && (
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 sm:gap-4 pl-3 sm:pl-4 border-l-2 border-blue-200">
                  <div>
                    <Label className="text-xs">Frequency</Label>
                    <Select value={formData.recurring_frequency} onValueChange={(v) => setFormData({ ...formData, recurring_frequency: v })}>
                      <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                        <SelectItem value="annually">Annually</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Start Date</Label>
                    <Input type="date" className="h-8 sm:h-9 text-xs sm:text-sm" value={formData.recurring_start_date} onChange={(e) => setFormData({ ...formData, recurring_start_date: e.target.value })} />
                  </div>
                  <div>
                    <Label className="text-xs">End Date (opt.)</Label>
                    <Input type="date" className="h-8 sm:h-9 text-xs sm:text-sm" value={formData.recurring_end_date} onChange={(e) => setFormData({ ...formData, recurring_end_date: e.target.value })} />
                  </div>
                </div>
              )}

              <div>
                <Label className="text-xs sm:text-sm">Notes</Label>
                <Textarea className="text-xs sm:text-sm" value={formData.notes} onChange={(e) => setFormData({...formData, notes: e.target.value})} placeholder="Optional notes..." />
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" className="h-8 sm:h-9 text-xs sm:text-sm" onClick={() => { setDialogOpen(false); setEditingInvoice(false); resetForm(); }}>Cancel</Button>
                <Button type="submit" className="h-8 sm:h-9 text-xs sm:text-sm">{editingInvoice ? 'Update Invoice' : 'Create Invoice'}</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Search & Filter Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex items-center gap-2 flex-1">
          <Search className="h-5 w-5 text-gray-400 hidden sm:block" />
          <Input placeholder="Search invoices..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="w-full sm:max-w-sm" data-testid="invoice-search" />
        </div>
        <Button
          variant={showFilters ? 'default' : 'outline'}
          onClick={() => setShowFilters(!showFilters)}
          data-testid="invoice-filter-toggle"
          className={showFilters ? 'bg-[#1a5490] hover:bg-[#0f3a5f]' : ''}
        >
          <Filter className="h-4 w-4 mr-2" />
          Filters
          {hasActiveFilters && (
            <Badge className="ml-2 bg-white/15 text-[#37c9ee]">{[filters.status, filters.customer_id, filters.is_recurring].filter(Boolean).length}</Badge>
          )}
        </Button>
      </div>

      {/* Filters Panel */}
      {showFilters && (
        <Card className="bg-gray-50">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs font-semibold mb-1 block">Status</Label>
                <Select value={filters.status || 'all'} onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                  <SelectTrigger className="h-9" data-testid="filter-invoice-status">
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    <SelectItem value="unpaid">Unpaid (All)</SelectItem>
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="sent">Sent</SelectItem>
                    <SelectItem value="paid">Paid</SelectItem>
                    <SelectItem value="overdue">Overdue</SelectItem>
                    <SelectItem value="cancelled">Cancelled</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Customer</Label>
                <Select value={filters.customer_id || undefined} onValueChange={(v) => setFilters({ ...filters, customer_id: v || '' })}>
                  <SelectTrigger className="h-9" data-testid="filter-invoice-customer">
                    <SelectValue placeholder="All customers" />
                  </SelectTrigger>
                  <SelectContent>
                    {customers.map(c => (
                      <SelectItem key={c.id} value={c.id}>{c.company_name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-semibold mb-1 block">Recurring</Label>
                <Select value={filters.is_recurring || undefined} onValueChange={(v) => setFilters({ ...filters, is_recurring: v || '' })}>
                  <SelectTrigger className="h-9" data-testid="filter-invoice-recurring">
                    <SelectValue placeholder="All invoices" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="true">Recurring Only</SelectItem>
                    <SelectItem value="false">One-time Only</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end">
                {hasActiveFilters && (
                  <Button size="sm" variant="outline" onClick={clearFilters} className="h-9">
                    <X className="h-3 w-3 mr-1" /> Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payment Reminders Settings */}
      <Card className="border-dashed" data-testid="payment-reminders-card">
        <CardHeader className="py-2 cursor-pointer" onClick={() => setReminderSettingsOpen(o => !o)}>
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm flex items-center gap-2 text-gray-700">
              <Mail className="h-4 w-4 text-[#1a5490]" />
              Payment Reminders
            </CardTitle>
            <div className="flex items-center gap-2">
              {reminderConfig?.enabled && <Badge variant="outline" className="text-[10px] border-emerald-300 text-emerald-700 bg-emerald-50">Active</Badge>}
              <ChevronRight className={`h-4 w-4 text-gray-400 transition-transform ${reminderSettingsOpen ? 'rotate-90' : ''}`} />
            </div>
          </div>
        </CardHeader>
        {reminderSettingsOpen && reminderConfig && (
          <CardContent className="pt-0 pb-3 space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-gray-500">Automatically send branded email reminders for unpaid invoices.</p>
                <p className="text-xs text-gray-400 mt-0.5">Customers can be individually opted out from their profile.</p>
              </div>
              <button
                onClick={() => saveReminderConfig({ enabled: !reminderConfig.enabled })}
                className={`relative w-10 h-5 rounded-full transition-colors ${reminderConfig.enabled ? 'bg-emerald-500' : 'bg-gray-300'}`}
                data-testid="reminder-global-toggle"
              >
                <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${reminderConfig.enabled ? 'translate-x-5' : 'translate-x-0.5'}`} />
              </button>
            </div>
            {reminderConfig.enabled && (
              <div className="flex flex-wrap gap-2" data-testid="reminder-schedule-chips">
                {(reminderConfig.schedule || []).map((rule, i) => (
                  <Badge key={i} variant="outline" className="text-xs py-1 px-2 gap-1.5">
                    {rule.days_offset < 0 ? `${Math.abs(rule.days_offset)}d before` : rule.days_offset === 0 ? 'On due date' : `${rule.days_offset}d overdue`}
                    <button onClick={() => {
                      const next = (reminderConfig.schedule || []).filter((_, j) => j !== i);
                      saveReminderConfig({ schedule: next });
                    }} className="text-gray-400 hover:text-red-500"><X className="h-2.5 w-2.5" /></button>
                  </Badge>
                ))}
                <Select onValueChange={(v) => {
                  const offset = parseInt(v);
                  const existing = (reminderConfig.schedule || []).map(r => r.days_offset);
                  if (existing.includes(offset)) { toast.error('Already added'); return; }
                  const label = offset < 0 ? `${Math.abs(offset)} days before due` : offset === 0 ? 'On due date' : `${offset} days overdue`;
                  saveReminderConfig({ schedule: [...(reminderConfig.schedule || []), { days_offset: offset, label }] });
                }}>
                  <SelectTrigger className="w-[130px] h-7 text-xs" data-testid="add-reminder-rule"><SelectValue placeholder="+ Add rule" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="-7">7d before</SelectItem>
                    <SelectItem value="-3">3d before</SelectItem>
                    <SelectItem value="-1">1d before</SelectItem>
                    <SelectItem value="0">On due date</SelectItem>
                    <SelectItem value="1">1d overdue</SelectItem>
                    <SelectItem value="3">3d overdue</SelectItem>
                    <SelectItem value="7">7d overdue</SelectItem>
                    <SelectItem value="14">14d overdue</SelectItem>
                    <SelectItem value="30">30d overdue</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* Invoices Table */}
      <Card>
        <CardContent className="p-3 sm:p-6">
          {/* Bulk Action Toolbar */}
          {selectedIds.size > 0 && (
            <div className="flex items-center gap-3 mb-4 p-3 pl-4 border-l-4 border-l-cyan-400 bg-cyan-50 border border-cyan-200 rounded-lg dark:bg-cyan-950/40 dark:border-cyan-800 focus-within:ring-2 focus-within:ring-cyan-400" role="region" aria-label="Bulk actions" data-testid="bulk-action-toolbar">
              <span className="text-sm font-semibold text-cyan-900 dark:text-cyan-100" data-testid="bulk-selected-count" aria-live="polite">{selectedIds.size} selected</span>
              <Separator orientation="vertical" className="h-5" />
              <Select value={bulkAction} onValueChange={setBulkAction}>
                <SelectTrigger className="w-[200px] h-8" data-testid="bulk-action-select">
                  <SelectValue placeholder="Choose action..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="mark_paid"><span className="flex items-center gap-1.5"><DollarSign className="h-3 w-3" /> Mark as Paid</span></SelectItem>
                  <SelectItem value="mark_sent"><span className="flex items-center gap-1.5"><Send className="h-3 w-3" /> Mark as Sent</span></SelectItem>
                  <SelectItem value="apply_credit"><span className="flex items-center gap-1.5"><CreditCard className="h-3 w-3" /> Apply Credit</span></SelectItem>
                  <SelectItem value="void"><span className="flex items-center gap-1.5"><Ban className="h-3 w-3" /> Void / Cancel</span></SelectItem>
                  <SelectItem value="delete"><span className="flex items-center gap-1.5"><Trash2 className="h-3 w-3" /> Delete</span></SelectItem>
                  <SelectItem value="send_reminder"><span className="flex items-center gap-1.5"><Mail className="h-3 w-3" /> Send Reminder</span></SelectItem>
                  <SelectItem value="export_csv"><span className="flex items-center gap-1.5"><Download className="h-3 w-3" /> Export CSV</span></SelectItem>
                </SelectContent>
              </Select>
              {bulkAction === 'mark_paid' && (
                <Input
                  placeholder="Payment method (optional)"
                  value={bulkPaymentMethod}
                  onChange={(e) => setBulkPaymentMethod(e.target.value)}
                  className="w-[180px] h-8"
                  data-testid="bulk-payment-method"
                />
              )}
              <Button
                size="sm"
                disabled={!bulkAction || bulkLoading}
                onClick={confirmBulkAction}
                className="h-8"
                data-testid="bulk-action-execute"
              >
                {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                {bulkAction ? bulkActionLabels[bulkAction] || 'Execute' : 'Select action'}
              </Button>
              <Button variant="ghost" size="sm" className="h-8 ml-auto focus-visible:ring-2 focus-visible:ring-cyan-400" onClick={() => { setSelectedIds(new Set()); setBulkAction(''); }} data-testid="bulk-clear-selection">
                <X className="h-3 w-3 mr-1" /> Clear
              </Button>
            </div>
          )}

          {/* Bulk Confirm Dialog */}
          <Dialog open={bulkConfirmOpen} onOpenChange={setBulkConfirmOpen}>
            <DialogContent className="co-scope co-settings sm:max-w-md" data-testid="bulk-confirm-dialog">
              <DialogHeader>
                <DialogTitle>Confirm Bulk Action</DialogTitle>
              </DialogHeader>
              <p className="text-sm text-gray-600">
                Are you sure you want to <strong>{bulkActionLabels[bulkAction]?.toLowerCase()}</strong> {selectedIds.size} invoice(s)?
                {bulkAction === 'delete' && <span className="text-red-600 block mt-1">This action cannot be undone.</span>}
                {bulkAction === 'send_reminder' && <span className="block mt-1">Payment reminder emails will be sent using your branded template.</span>}
              </p>
              <div className="flex justify-end gap-2 mt-4">
                <Button variant="outline" onClick={() => setBulkConfirmOpen(false)}>Cancel</Button>
                <Button
                  variant={bulkAction === 'delete' ? 'destructive' : 'default'}
                  onClick={handleBulkAction}
                  disabled={bulkLoading}
                  data-testid="bulk-confirm-execute"
                >
                  {bulkLoading ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
                  {bulkActionLabels[bulkAction] || 'Confirm'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Mobile Card View */}
          <div className="md:hidden space-y-2" data-testid="invoices-mobile-cards">
            {invoices.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                No invoices found
              </div>
            ) : invoices.map((invoice) => (
              <Card
                key={invoice.id}
                aria-selected={selectedIds.has(invoice.id)}
                data-state={selectedIds.has(invoice.id) ? 'selected' : undefined}
                className={`cursor-pointer hover:shadow-sm transition-shadow border-l-4 ${selectedIds.has(invoice.id) ? CYAN_ROW_SELECTED : ''}`}
                style={{ borderLeftColor: invoice.status === 'paid' ? '#22c55e' : invoice.status === 'overdue' ? '#ef4444' : '#3b82f6' }}
                onClick={() => handleViewInvoice(invoice)}
                data-testid={`invoice-card-${invoice.id}`}
              >
                <CardContent className="p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-start gap-2 min-w-0 flex-1">
                      <Checkbox
                        checked={selectedIds.has(invoice.id)}
                        onCheckedChange={() => toggleSelect(invoice.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Select invoice ${invoice.invoice_number}`}
                        className={`mt-1 shrink-0 ${CYAN_CHECK}`}
                      />
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-mono text-sm font-semibold">{invoice.invoice_number}</span>
                          <Badge variant="custom" className={`text-xs ${getStatusColor(invoice.status)}`}>{invoice.status}</Badge>
                        </div>
                        <p className="text-sm text-muted-foreground truncate">{invoice.customer_name || '-'}</p>
                      </div>
                    </div>
                    <span className="font-semibold text-base whitespace-nowrap">${fmtMoney(invoice.total || 0)}</span>
                  </div>
                  <div className="flex items-center justify-between mt-2 text-xs text-muted-foreground pl-8">
                    <span>Issued {formatDate(invoice.issue_date)} &middot; Due {formatDate(invoice.due_date)}</span>
                    {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                      <Select value={invoice.status} onValueChange={(v) => handleStatusChange(invoice.id, v)}>
                        <SelectTrigger className="w-[80px] h-7 text-xs" onClick={(e) => e.stopPropagation()}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="draft">Draft</SelectItem>
                          <SelectItem value="sent">Sent</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="cancelled">Cancel</SelectItem>
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Desktop View — Grouped by Customer, all collapsed by default */}
          <div className="hidden md:block" data-testid="invoices-grouped-view">
          {invoices.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-2" />
              No invoices found
            </div>
          ) : (() => {
            const grouped = {};
            invoices.forEach(inv => { const k = inv.customer_name || 'No Customer'; if (!grouped[k]) grouped[k] = []; grouped[k].push(inv); });
            const groupNames = Object.keys(grouped).sort((a, b) => { if (a === 'No Customer') return 1; if (b === 'No Customer') return -1; return a.localeCompare(b); });
            const sortGroupInvoices = (list) => [...list].sort((a, b) => compareInvoices(a, b, groupSort.field, groupSort.order));
            const toggleGroupSort = (field) => setGroupSort(prev => prev.field === field ? { field, order: prev.order === 'asc' ? 'desc' : 'asc' } : { field, order: 'asc' });
            const GSI = ({ field }) => { if (groupSort.field !== field) return <ArrowUpDown className="h-3 w-3 text-gray-300 ml-0.5" />; return groupSort.order === 'asc' ? <ArrowUp className="h-3 w-3 text-blue-600 ml-0.5" /> : <ArrowDown className="h-3 w-3 text-blue-600 ml-0.5" />; };
            const SortTH = ({ field, label }) => (
              <TableHead
                role="button" tabIndex={0}
                aria-sort={groupSort.field === field ? (groupSort.order === 'asc' ? 'ascending' : 'descending') : 'none'}
                className="text-xs font-medium cursor-pointer select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 rounded"
                onClick={() => toggleGroupSort(field)}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleGroupSort(field); } }}
                data-testid={`sort-${field}`}
              ><div className="flex items-center">{label} <GSI field={field} /></div></TableHead>
            );
            return (
              <div className="space-y-2">
                <div className="flex items-center justify-between text-xs text-gray-500 px-1 mb-1">
                  <span>{groupNames.length} customer{groupNames.length !== 1 ? 's' : ''} · {invoices.length} invoice{invoices.length !== 1 ? 's' : ''}</span>
                  <div className="flex gap-2">
                    <button className="hover:text-gray-800 hover:underline" onClick={() => { const all = {}; groupNames.forEach(n => { all[n] = true; }); setExpandedGroups(all); }}>Expand all</button>
                    <button className="hover:text-gray-800 hover:underline" onClick={() => setExpandedGroups({})}>Collapse all</button>
                  </div>
                </div>
                {groupNames.map(customerName => {
                  const custInvoices = grouped[customerName];
                  const isExpanded = expandedGroups[customerName] === true;
                  const custTotal = custInvoices.reduce((s, i) => s + (i.total || 0), 0);
                  const unpaidCount = custInvoices.filter(i => !['paid', 'cancelled'].includes(i.status)).length;
                  return (
                    <Card key={customerName} className="overflow-hidden" data-testid={`invoice-group-${customerName}`}>
                      <button type="button" className="w-full flex items-center justify-between px-4 py-3 bg-neutral-50 hover:bg-neutral-100 transition-colors text-left border-b" onClick={() => setExpandedGroups(prev => ({ ...prev, [customerName]: !prev[customerName] }))}>
                        <div className="flex items-center gap-2">
                          <ChevronRightIcon className={`h-4 w-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} />
                          <Users className="h-4 w-4 text-gray-500" />
                          <span className="font-semibold text-sm">{customerName}</span>
                          <Badge variant="secondary" className="text-xs ml-1">{custInvoices.length}</Badge>
                        </div>
                        <div className="flex items-center gap-3 text-xs text-gray-500">
                          {unpaidCount > 0 && <span className="text-amber-600 font-medium">{unpaidCount} unpaid</span>}
                          <span className="font-semibold text-gray-700">${fmtMoney(custTotal)}</span>
                        </div>
                      </button>
                      {isExpanded && (
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead className="w-10"><Checkbox aria-label={`Select all invoices for ${customerName}`} className={CYAN_CHECK} checked={groupCheckedValue(custInvoices, selectedIds)} onCheckedChange={() => setSelectedIds(toggleGroup(custInvoices, selectedIds))} /></TableHead>
                              <SortTH field="invoice_number" label="Invoice #" />
                              <SortTH field="issue_date" label="Issue Date" />
                              <SortTH field="due_date" label="Due Date" />
                              <SortTH field="total" label="Amount" />
                              <SortTH field="status" label="Status" />
                              <TableHead className="text-xs font-medium">Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {sortGroupInvoices(custInvoices).map(invoice => (
                              <TableRow key={invoice.id} aria-selected={selectedIds.has(invoice.id)} data-state={selectedIds.has(invoice.id) ? 'selected' : undefined} className={`cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-800/40 ${selectedIds.has(invoice.id) ? CYAN_ROW_SELECTED : ''}`} onClick={() => handleViewInvoice(invoice)} data-testid={`invoice-row-${invoice.id}`}>
                                <TableCell onClick={(e) => e.stopPropagation()}><Checkbox aria-label={`Select invoice ${invoice.invoice_number}`} className={CYAN_CHECK} checked={selectedIds.has(invoice.id)} onCheckedChange={() => toggleSelect(invoice.id)} /></TableCell>
                                <TableCell className="font-mono text-sm">{invoice.invoice_number}{invoice.is_recurring && <Repeat className="inline h-3.5 w-3.5 ml-1.5 text-blue-500" />}</TableCell>
                                <TableCell className="text-sm">{formatDate(invoice.issue_date)}</TableCell>
                                <TableCell className="text-sm">{formatDate(invoice.due_date)}</TableCell>
                                <TableCell className="font-semibold">${fmtMoney(invoice.total || 0)}</TableCell>
                                <TableCell><Badge variant="custom" className={getStatusColor(invoice.status)}>{invoice.status}</Badge></TableCell>
                                <TableCell onClick={(e) => e.stopPropagation()}>
                                  <div className="flex gap-1">
                                    {invoice.status !== 'paid' && invoice.status !== 'cancelled' && (
                                      <Select value={invoice.status} onValueChange={(v) => handleStatusChange(invoice.id, v)}>
                                        <SelectTrigger className="w-[100px] h-8"><SelectValue /></SelectTrigger>
                                        <SelectContent>
                                          <SelectItem value="draft">Draft</SelectItem>
                                          <SelectItem value="sent">Sent</SelectItem>
                                          <SelectItem value="paid">Paid</SelectItem>
                                          <SelectItem value="cancelled">Cancel</SelectItem>
                                        </SelectContent>
                                      </Select>
                                    )}
                                    <Button size="sm" variant="ghost" onClick={() => handleDelete(invoice.id)}><Trash2 className="h-4 w-4 text-red-600" /></Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            ))}
                          </TableBody>
                        </Table>
                      )}
                    </Card>
                  );
                })}
              </div>
            );
          })()}
          </div>

          {/* Pagination */}
          {invoices.length > 0 && (
            <div data-testid="invoices-pagination" className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-4 border-t mt-4">
              <div className="text-xs sm:text-sm text-neutral-500">
                Showing {currentPage * pageSize + 1}–{Math.min((currentPage + 1) * pageSize, totalInvoices)} of {totalInvoices}
              </div>
              <div className="flex items-center gap-2 sm:gap-3 flex-wrap">
                <div className="flex items-center gap-2">
                  <span className="text-xs sm:text-sm text-neutral-500">Per page:</span>
                  <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(v === 'all' ? 10000 : parseInt(v)); setCurrentPage(0); }}>
                    <SelectTrigger className="w-[80px] h-8" data-testid="invoice-page-size"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="10">10</SelectItem>
                      <SelectItem value="25">25</SelectItem>
                      <SelectItem value="50">50</SelectItem>
                      <SelectItem value="10000">All</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(0)} className="h-8 px-2">First</Button>
                  <Button variant="outline" size="sm" disabled={currentPage === 0} onClick={() => setCurrentPage(p => Math.max(0, p - 1))} className="h-8 w-8 p-0"><ChevronLeft className="h-4 w-4" /></Button>
                  <span className="text-sm text-neutral-600 px-2 min-w-[80px] text-center">Page {currentPage + 1} of {totalPages}</span>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(p => p + 1)} className="h-8 w-8 p-0"><ChevronRight className="h-4 w-4" /></Button>
                  <Button variant="outline" size="sm" disabled={currentPage >= totalPages - 1} onClick={() => setCurrentPage(totalPages - 1)} className="h-8 px-2">Last</Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invoice Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="co-scope co-settings w-[95vw] max-w-5xl max-h-[90vh] overflow-y-auto p-3 sm:p-6">
          <DialogHeader>
            <DialogTitle className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <span className="text-sm sm:text-lg">Invoice {selectedInvoice?.invoice_number}</span>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="h-7 sm:h-8 text-xs sm:text-sm" onClick={async () => {
                  try {
                    const r = await api.get(`/export/invoices/${selectedInvoice?.id}/pdf?preview=true`, { responseType: 'blob' });
                    const url = window.URL.createObjectURL(new Blob([r.data], { type: 'application/pdf' }));
                    setModalPreviewUrl(url); setModalPreviewOpen(true);
                  } catch { toast.error('Failed to load preview'); }
                }} data-testid="modal-preview-invoice-btn">
                  <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span>Preview</span>
                </Button>
                <InvoiceSendButton
                  invoice={selectedInvoice}
                  onSent={() => { setSelectedInvoice(prev => prev ? { ...prev, status: 'sent' } : prev); loadInvoices(); }}
                />
                <Button variant="outline" size="sm" className="h-7 sm:h-8 text-xs sm:text-sm" onClick={() => { setDetailDialogOpen(false); navigate(`/invoices/${selectedInvoice?.id}?from=${buildReturnParam(location)}`); }} data-testid="open-invoice-full-page">
                  <ExternalLink className="h-3 w-3 sm:h-4 sm:w-4 mr-1" />
                  <span className="hidden sm:inline">Full Page</span>
                  <span className="sm:hidden">Full</span>
                </Button>
                <Button variant="outline" size="sm" className="h-7 sm:h-8 text-xs sm:text-sm" onClick={handleEditInvoice}>
                  <Edit className="h-3 w-3 sm:h-4 sm:w-4 mr-1" /> Edit
                </Button>
              </div>
            </DialogTitle>
          </DialogHeader>

          {selectedInvoice && (
            <div className="space-y-3 sm:space-y-4 mt-3 sm:mt-4">
              <div className="grid grid-cols-2 gap-2 sm:gap-4">
                <div>
                  <Label className="text-xs sm:text-sm text-gray-500">Customer</Label>
                  <p className="font-semibold text-sm sm:text-base">
                    {selectedInvoice.customer_id ? (
                      <Link
                        to={`/customers/${selectedInvoice.customer_id}`}
                        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1"
                        onClick={() => setDetailDialogOpen(false)}
                        data-testid="invoice-customer-link"
                      >
                        <User className="h-3 w-3 sm:h-4 sm:w-4" />
                        <span className="truncate">{selectedInvoice.customer_name || 'View'}</span>
                      </Link>
                    ) : (
                      <span className="truncate">{selectedInvoice.customer_name || '-'}</span>
                    )}
                  </p>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm text-gray-500">Status</Label>
                  <div className="flex items-center gap-1 sm:gap-2 flex-wrap">
                    <Badge variant="custom" className={`text-[10px] sm:text-xs ${getStatusColor(selectedInvoice.status)}`}>{selectedInvoice.status}</Badge>
                    <Select value={selectedInvoice.status} onValueChange={(v) => handleStatusChange(selectedInvoice.id, v)}>
                      <SelectTrigger className="w-[90px] sm:w-[120px] h-7 sm:h-8 text-xs"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="draft">Draft</SelectItem>
                        <SelectItem value="sent">Sent</SelectItem>
                        <SelectItem value="paid">Paid</SelectItem>
                        <SelectItem value="cancelled">Cancelled</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm text-gray-500">Issue Date</Label>
                  <p className="text-xs sm:text-sm">{formatDate(selectedInvoice.issue_date)}</p>
                </div>
                <div>
                  <Label className="text-xs sm:text-sm text-gray-500">Due Date</Label>
                  <p className="text-xs sm:text-sm">{formatDate(selectedInvoice.due_date) || 'No due date'}</p>
                </div>
              </div>

              {/* Recurring Status & Action */}
              <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between p-2 sm:p-3 bg-neutral-50 rounded-lg border">
                <div className="flex items-center gap-2">
                  <Repeat className={`h-3 w-3 sm:h-4 sm:w-4 ${selectedInvoice.is_recurring ? 'text-blue-600' : 'text-neutral-400'}`} />
                  <span className="text-xs sm:text-sm font-medium">
                    {selectedInvoice.is_recurring
                      ? `Recurring: ${selectedInvoice.recurring_frequency || 'monthly'}`
                      : 'One-time'}
                  </span>
                </div>
                {selectedInvoice.is_recurring ? (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleRemoveRecurring(selectedInvoice.id)}
                    className="text-red-600 hover:text-red-700 h-7 text-xs"
                    data-testid="remove-recurring-btn"
                  >
                    <X className="h-3 w-3 mr-1" /> Remove
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      setRecurringData({
                        recurring_frequency: 'monthly',
                        recurring_start_date: new Date().toISOString().split('T')[0],
                        recurring_end_date: ''
                      });
                      setRecurringDialogOpen(true);
                    }}
                    className="text-blue-600 hover:text-blue-700 h-7 text-xs"
                    data-testid="make-recurring-btn"
                  >
                    <Repeat className="h-3 w-3 mr-1" /> Make Recurring
                  </Button>
                )}
              </div>

              {/* Credit Application */}
              {selectedInvoice.customer_id && (
                <div className="flex items-center justify-between gap-2 p-2 sm:p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4 text-emerald-700" />
                    <span className="text-xs sm:text-sm font-medium text-emerald-900">
                      {(selectedInvoice.credits_applied || 0) > 0
                        ? `Credits applied: $${fmtMoney(Number(selectedInvoice.credits_applied || 0))}`
                        : 'Apply customer credit to this invoice'}
                    </span>
                  </div>
                  {selectedInvoice.status !== 'paid' && selectedInvoice.status !== 'cancelled' && (
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white border border-transparent h-7 text-xs shrink-0 focus-visible:ring-2 focus-visible:ring-emerald-400 focus-visible:ring-offset-1 disabled:opacity-60 disabled:cursor-not-allowed"
                      onClick={() => handleApplyCreditToInvoice(selectedInvoice)}
                      disabled={applyingCredit}
                      data-testid="invoice-apply-credit-btn"
                    >
                      {applyingCredit ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <DollarSign className="h-3 w-3 mr-1" />}
                      Apply Credit
                    </Button>
                  )}
                </div>
              )}

              {/* Line Items */}
              <Card>
                <CardHeader className="p-3 sm:p-4 pb-2"><CardTitle className="text-sm sm:text-base">Line Items</CardTitle></CardHeader>
                <CardContent className="p-2 sm:p-4 pt-0">
                  {selectedInvoice.line_items?.length > 0 ? (
                    <>
                      {/* Mobile stacked view */}
                      <div className="sm:hidden space-y-2">
                        {selectedInvoice.line_items.map((item, idx) => (
                          <div key={idx} className="border rounded p-2.5">
                            <p className="text-sm font-medium">{item.description}</p>
                            <div className="flex items-center justify-between mt-1.5 text-xs text-muted-foreground">
                              <span>{item.quantity} x ${fmtPrice(item.unit_price || 0)}</span>
                              <span className="font-semibold text-sm text-foreground">${fmtMoney(item.amount || 0)}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                      {/* Desktop table view */}
                      <div className="hidden sm:block">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="text-xs">Description</TableHead>
                            <TableHead className="text-xs text-center w-[50px]">Qty</TableHead>
                            <TableHead className="text-xs text-right w-[70px]">Price</TableHead>
                            <TableHead className="text-xs text-right w-[80px]">Amount</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {selectedInvoice.line_items.map((item, idx) => (
                            <TableRow key={idx}>
                              <TableCell className="text-sm py-2">{item.description}</TableCell>
                              <TableCell className="text-sm text-center py-2">{item.quantity}</TableCell>
                              <TableCell className="text-sm text-right py-2">${fmtMoney(item.unit_price || 0)}</TableCell>
                              <TableCell className="text-sm text-right font-semibold py-2">${fmtMoney(item.amount || 0)}</TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    </>
                  ) : (
                    <p className="text-xs sm:text-sm text-gray-500 text-center py-3">No line items</p>
                  )}
                </CardContent>
              </Card>

              {/* Totals */}
              <Card>
                <CardContent className="p-3 sm:p-4 space-y-1 text-xs sm:text-sm">
                  <div className="flex justify-between"><span className="text-gray-600">Subtotal:</span><span className="font-semibold">${fmtMoney(selectedInvoice.subtotal || 0)}</span></div>
                  <div className="flex justify-between"><span className="text-gray-600">Tax:</span><span className="font-semibold">${fmtMoney(selectedInvoice.tax_amount || 0)}</span></div>
                  {(selectedInvoice.credits_applied || 0) > 0 && (
                    <div className="flex justify-between text-emerald-600"><span>Credits Applied:</span><span className="font-semibold">-${fmtMoney(selectedInvoice.credits_applied || 0)}</span></div>
                  )}
                  <div className="flex justify-between text-sm sm:text-lg font-bold border-t pt-2"><span>Total:</span><span>${fmtMoney(selectedInvoice.total || 0)}</span></div>
                  {(selectedInvoice.credits_applied || 0) > 0 && (
                    <div className="flex justify-between text-sm sm:text-base font-bold text-blue-700 bg-blue-50 rounded px-2 py-1 -mx-2">
                      <span>Balance Due:</span>
                      <span>${fmtMoney(Math.max((selectedInvoice.total || 0) - (selectedInvoice.credits_applied || 0), 0))}</span>
                    </div>
                  )}
                </CardContent>
              </Card>

              {selectedInvoice.notes && (
                <Card>
                  <CardHeader className="p-3 pb-1"><CardTitle className="text-sm">Notes</CardTitle></CardHeader>
                  <CardContent className="p-3 pt-1"><p className="text-xs sm:text-sm whitespace-pre-wrap">{selectedInvoice.notes}</p></CardContent>
                </Card>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Make Recurring Dialog */}
      <Dialog open={recurringDialogOpen} onOpenChange={setRecurringDialogOpen}>
        <DialogContent className="co-scope co-settings w-[95vw] max-w-md p-4 sm:p-6" data-testid="make-recurring-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-sm sm:text-base">
              <Repeat className="h-4 w-4 sm:h-5 sm:w-5 text-blue-600" />
              Make Invoice Recurring
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 sm:space-y-4 mt-2">
            <p className="text-xs sm:text-sm text-neutral-600">
              Set up auto generation for <strong>{selectedInvoice?.invoice_number}</strong>
            </p>
            <div>
              <Label className="text-xs sm:text-sm">Frequency</Label>
              <Select value={recurringData.recurring_frequency} onValueChange={(v) => setRecurringData({ ...recurringData, recurring_frequency: v })}>
                <SelectTrigger className="h-8 sm:h-9 text-xs sm:text-sm" data-testid="recurring-frequency"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly (3 months)</SelectItem>
                  <SelectItem value="annually">Annually</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs sm:text-sm">Start Date</Label>
              <Input type="date" className="h-8 sm:h-9 text-xs sm:text-sm" value={recurringData.recurring_start_date} onChange={(e) => setRecurringData({ ...recurringData, recurring_start_date: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs sm:text-sm">End Date (optional)</Label>
              <Input type="date" className="h-8 sm:h-9 text-xs sm:text-sm" value={recurringData.recurring_end_date} onChange={(e) => setRecurringData({ ...recurringData, recurring_end_date: e.target.value })} />
              <p className="text-[10px] sm:text-xs text-neutral-400 mt-1">Leave empty for no end</p>
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" className="h-8 sm:h-9 text-xs sm:text-sm" onClick={() => setRecurringDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleMakeRecurring} className="bg-[#1a5490] hover:bg-[#0f3a5f] h-8 sm:h-9 text-xs sm:text-sm">
                <Repeat className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2" /> Set Recurring
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={modalPreviewOpen} onOpenChange={(open) => { if (!open) { setModalPreviewOpen(false); if (modalPreviewUrl) { window.URL.revokeObjectURL(modalPreviewUrl); setModalPreviewUrl(null); } } }}>
        <DialogContent className="co-scope co-settings max-w-5xl h-[90vh] p-0" data-testid="modal-pdf-preview">
          <div className="flex items-center justify-between px-4 py-2 border-b bg-gray-50">
            <span className="text-sm font-semibold">Invoice Preview</span>
            <Button size="sm" variant="ghost" onClick={() => setModalPreviewOpen(false)}>
              <X className="h-4 w-4" />
            </Button>
          </div>
          {modalPreviewUrl && (
            <iframe src={modalPreviewUrl} className="w-full" style={{ height: 'calc(90vh - 50px)', border: 'none' }} title="Invoice PDF Preview" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
